
import { Component, Vue } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import Fields from './Fields.vue';
import Paths from './Paths.vue';
import FieldStore from '@/store/modules/fields';
import PathStore from '@/store/modules/paths';
import LastModifiedMixIn from '@/mixins/lastModified';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Container } from 'inversify';

@Component({ name: 'FieldsAndPaths', inject: ['container'], components: { Fields, Paths } })
export default class FieldsAndPaths extends Vue {
  fieldStore = getModule(FieldStore, this.$store);
  pathStore = getModule(PathStore, this.$store);
  mode: 'field' | 'path' = 'field';
  selectedId = '';
  // Only path selection opens a group, a click on a field already toggles its own
  openGroup = '';
  uploadFieldId = '';
  loading = true;
  toast = false;
  toastMessage = '';
  windowSize = { x: 0, y: 0 };
  private container!: Container;

  async mounted() {
    this.onResize();
    // The panes are only rendered on desktop
    if (!this.$refs.fields) return;
    try {
      await this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH).updateToken();
      await Promise.all([(this.$refs.fields as Fields).getAvailableFields(), (this.$refs.paths as Paths).getCurrentPaths()]);
    } finally {
      this.loading = false;
    }
  }

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  get groups() {
    const paths = this.pathStore.availablePaths;
    const groups: { field: FieldOperationsModel | null; paths: SimplePath[] }[] = this.fieldStore.availableFields.map((field) => ({
      field,
      paths: paths.filter((path) => path.FieldID == field.FieldID)
    }));
    const orphans = paths.filter((path) => !groups.some((group) => group.field?.FieldID == path.FieldID));
    return orphans.length ? [...groups, { field: null, paths: orphans }] : groups;
  }

  groupKey(group: { field: FieldOperationsModel | null }) {
    return group.field ? group.field.FieldID : 'orphans';
  }

  get editing() {
    return this.fieldStore.editFieldMode || this.pathStore.edit;
  }

  // The panes own the "seen" state, so the badges read it from them
  isNew(pane: 'fields' | 'paths', date: Date, id: string) {
    return (this.$refs[pane] as LastModifiedMixIn | undefined)?.isNew(date, id) ?? false;
  }

  selectField(field: FieldOperationsModel) {
    if (this.editing) return;
    this.mode = 'field';
    this.selectedId = field.FieldID;
    (this.$refs.fields as Fields).selectField(field);
  }

  async selectPath(path: SimplePath) {
    if (this.editing) return;
    this.selectedId = path.PathID;
    const group = this.groups.find((value) => value.paths.some((item) => item.PathID == path.PathID));
    if (group) this.openGroup = this.groupKey(group);
    this.loading = true;
    await (this.$refs.paths as Paths).selectPath(path);
    this.loading = false;
    this.mode = 'path';
  }

  uploadPath(fieldId: string) {
    if (this.editing) return;
    this.uploadFieldId = fieldId;
    (this.$refs.uploader as HTMLInputElement).click();
  }

  async onFileChanged(e: Event) {
    const input = e.target as HTMLInputElement;
    this.loading = true;
    const path = await (this.$refs.paths as Paths).validateUpload((input.files as FileList)[0], this.uploadFieldId);
    this.loading = false;
    // Lets the same file be uploaded again to another field
    input.value = '';
    if (path) this.selectPath(path);
  }

  showToast(message: string) {
    this.toastMessage = message;
    this.toast = true;
  }

  onFieldDeleted(field: FieldOperationsModel) {
    this.selectedId = '';
    this.showToast(this.$t('views.fields.dialog.field_deleted', { name: field.FieldName }).toString());
  }

  onPathDeleted(path: SimplePath) {
    this.selectedId = '';
    this.showToast(this.$t('views.paths.dialog.path_deleted', { name: path.Name }).toString());
    const field = this.fieldStore.availableFields.find((value) => value.FieldID == path.FieldID);
    if (field) this.selectField(field);
  }

  addField() {
    this.mode = 'field';
    (this.$refs.fields as Fields).resetForm();
  }
}
